import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import {
  getTarifasReguladas,
  saveTarifaRegulada,
  type ComponenteCatalogo,
  type NivelTension,
  type NivelTensionCatalogo,
  type TarifaReguladaAnio,
} from "@/lib/api";
import { fmtMxnMwh, formatFecha } from "@/lib/utils";

const CURRENT_YEAR = 2026; // año de operación de los contratos vigentes (Sprint 1)
const NIVEL_DEFAULT: NivelTension = "lt220";

/** Suma de los valores (string) del formulario, ignorando vacíos/no numéricos. */
function sumValores(values: Record<string, string>): number {
  return Object.values(values).reduce((acc, v) => {
    const n = Number(v);
    return Number.isFinite(n) ? acc + n : acc;
  }, 0);
}

export function TarifasReguladasPage() {
  const qc = useQueryClient();
  const listQ = useQuery({
    queryKey: ["tarifas-reguladas"],
    queryFn: () => getTarifasReguladas(),
  });

  const catalogo: ComponenteCatalogo[] = listQ.data?.catalogo ?? [];
  const niveles: NivelTensionCatalogo[] = listQ.data?.niveles ?? [];
  const items: TarifaReguladaAnio[] = listQ.data?.items ?? [];
  const nivelLabel = (k: string) => niveles.find((n) => n.key === k)?.label ?? k;

  const [anio, setAnio] = useState<number>(CURRENT_YEAR);
  const [nivel, setNivel] = useState<NivelTension>(NIVEL_DEFAULT);
  const [values, setValues] = useState<Record<string, string>>({});
  const [notas, setNotas] = useState<string>("");
  const [okMsg, setOkMsg] = useState<string | null>(null);

  // Precarga del formulario con lo capturado para ese año + nivel (si existe).
  const loadAnio = useMemo(
    () => (yr: number, nv: NivelTension, data: TarifaReguladaAnio[]) => {
      const existing = data.find((it) => it.anio === yr && it.nivel_tension === nv);
      const next: Record<string, string> = {};
      for (const c of catalogo) {
        const found = existing?.componentes.find((x) => x.componente === c.key);
        next[c.key] = found ? String(found.valor_mxn_mwh) : "";
      }
      setValues(next);
      setNotas(existing?.notas ?? "");
    },
    [catalogo],
  );

  // Cuando llegan datos o cambia el año/nivel, reprecargar.
  useEffect(() => {
    if (!listQ.data) return;
    loadAnio(anio, nivel, listQ.data.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQ.data, anio, nivel]);

  const mutation = useMutation({
    mutationFn: saveTarifaRegulada,
    onSuccess: (saved) => {
      setOkMsg(
        `Guardado ${saved.anio} · ${nivelLabel(saved.nivel_tension)} · ` +
          `total ${fmtMxnMwh(saved.total_mxn_mwh)} MXN/MWh`,
      );
      qc.invalidateQueries({ queryKey: ["tarifas-reguladas"] });
      setTimeout(() => setOkMsg(null), 4000);
    },
  });

  const total = sumValores(values);
  const algunValor = Object.values(values).some((v) => v.trim() !== "");

  const handleSave = () => {
    const componentes = catalogo
      .map((c) => ({ componente: c.key, valor: values[c.key]?.trim() ?? "" }))
      .filter((c) => c.valor !== "")
      .map((c) => ({ componente: c.componente, valor_mxn_mwh: Number(c.valor) }));

    if (componentes.length === 0) {
      mutation.reset();
      return;
    }
    if (componentes.some((c) => !Number.isFinite(c.valor_mxn_mwh) || c.valor_mxn_mwh < 0)) {
      return;
    }
    mutation.mutate({ anio, nivel_tension: nivel, componentes, notas: notas.trim() || null });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Tarifas Reguladas del Generador (TReg)</h1>
        <p className="text-sm text-slate-500 mt-1">
          Cargos regulados anuales (MXN/MWh) que entran a la Activación TBFin y al Cargo CTreg.
          Captura desglosada por componente y por <strong>nivel de tensión</strong> del generador
          (la CNE publica una transmisión distinta para &lt;220 kV y para &gt;=220 kV); el TReg
          total es la suma de los componentes de ese nivel. Qué nivel le toca a cada contrato lo
          dice la cláusula de Treg de su OC y se define en su ficha (Contratos).
        </p>
      </div>

      {/* ---- Formulario de captura ---- */}
      <div className="bg-white border rounded-lg p-5 space-y-4">
        <div className="flex items-end gap-4 flex-wrap">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Año de operación</span>
            <input
              type="number"
              min={2016}
              max={2100}
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
              className="mt-1 border rounded px-3 py-1.5 w-32 tabular-nums"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Nivel de tensión</span>
            <select
              value={nivel}
              onChange={(e) => setNivel(e.target.value as NivelTension)}
              className="mt-1 border rounded px-3 py-1.5 w-40 block"
            >
              {(niveles.length ? niveles : [{ key: NIVEL_DEFAULT, label: "< 220 kV" }]).map((n) => (
                <option key={n.key} value={n.key}>
                  {n.label}
                </option>
              ))}
            </select>
          </label>
          <div className="text-xs text-slate-500">
            {items.some((it) => it.anio === anio && it.nivel_tension === nivel)
              ? "Editando una captura existente."
              : "Sin captura previa para ese año y nivel."}
            <div className="text-slate-400">
              {niveles.find((n) => n.key === nivel)?.descripcion}
            </div>
          </div>
        </div>

        <table className="w-full text-sm border-t">
          <thead className="text-slate-500 text-left">
            <tr>
              <th className="py-2 font-medium">Componente</th>
              <th className="py-2 font-medium w-48 text-right">Valor (MXN/MWh)</th>
            </tr>
          </thead>
          <tbody>
            {catalogo.map((c) => (
              <tr key={c.key} className="border-t">
                <td className="py-2">
                  <div className="font-medium text-slate-800">{c.label}</div>
                  <div className="text-xs text-slate-400">{c.descripcion}</div>
                </td>
                <td className="py-2 text-right">
                  <input
                    type="number"
                    step="0.00001"
                    min={0}
                    inputMode="decimal"
                    value={values[c.key] ?? ""}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [c.key]: e.target.value }))
                    }
                    placeholder="0.00000"
                    className="border rounded px-2 py-1.5 w-40 text-right tabular-nums"
                  />
                </td>
              </tr>
            ))}
            <tr className="border-t bg-slate-50">
              <td className="py-2 font-semibold text-slate-700">TReg total</td>
              <td className="py-2 text-right font-semibold tabular-nums">
                {fmtMxnMwh(total)}
              </td>
            </tr>
          </tbody>
        </table>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Notas / referencia del acuerdo CNE</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            placeholder={`ej. Acuerdo CNE A/XXX/${anio}, generador ${nivelLabel(nivel)}`}
            className="mt-1 border rounded px-3 py-2 w-full text-sm"
          />
        </label>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={!algunValor || mutation.isPending}
            className="inline-flex items-center gap-2 bg-blue-600 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {mutation.isPending ? "Guardando…" : "Guardar año"}
          </button>
          {okMsg && <span className="text-sm text-emerald-600">{okMsg}</span>}
          {mutation.isError && (
            <span className="text-sm text-red-600">
              Error: {(mutation.error as Error).message}
            </span>
          )}
        </div>
      </div>

      {/* ---- Histórico capturado ---- */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b">
          <h2 className="text-sm font-semibold text-slate-800">Años capturados</h2>
          <p className="text-xs text-slate-400 mt-0.5">Una fila por año y nivel de tensión.</p>
        </div>
        {listQ.isLoading && (
          <div className="p-8 text-center text-slate-500 text-sm">Cargando…</div>
        )}
        {listQ.isError && (
          <div className="p-8 text-center text-red-600 text-sm">
            Error: {(listQ.error as Error).message}
          </div>
        )}
        {!listQ.isLoading && !listQ.isError && items.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">
            Aún no hay tarifas capturadas.
          </div>
        )}
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Año</th>
                <th className="px-3 py-2 font-medium">Nivel de tensión</th>
                {catalogo.map((c) => (
                  <th key={c.key} className="px-3 py-2 font-medium text-right">
                    {c.label}
                  </th>
                ))}
                <th className="px-3 py-2 font-medium text-right">TReg total</th>
                <th className="px-3 py-2 font-medium">Actualizado</th>
                <th className="px-3 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const byKey = Object.fromEntries(
                  it.componentes.map((c) => [c.componente, c.valor_mxn_mwh]),
                );
                return (
                  <tr key={`${it.anio}-${it.nivel_tension}`} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium tabular-nums">{it.anio}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {nivelLabel(it.nivel_tension)}
                      </span>
                    </td>
                    {catalogo.map((c) => (
                      <td key={c.key} className="px-3 py-2 text-right tabular-nums">
                        {byKey[c.key] != null ? fmtMxnMwh(byKey[c.key]) : "—"}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">
                      {fmtMxnMwh(it.total_mxn_mwh)}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      <div>{formatFecha(it.actualizado_en)}</div>
                      <div className="text-slate-400">{it.actualizado_por ?? ""}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setAnio(it.anio);
                          setNivel(it.nivel_tension);
                        }}
                        className="inline-flex items-center gap-1 text-xs border rounded px-2 py-1 hover:bg-slate-100"
                      >
                        <Pencil className="w-3 h-3" /> Editar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
